import { useState } from "react";
import { useVerifyProposedBankDetails } from "creso-core/hooks";
import { UnboxPromise } from "creso-core/types";
import { sleep } from "creso-core/utils";
import { APIError } from "../../utils";

const MAX_VERIFICATION_ATTEMPTS = 5;

const BANK_VERIFICATION_FAILURE_STATUSES = [
  "limit_reached",
  "failed",
  "name_mismatch",
  "no_change",
];

export function useBankVerification() {
  const { mutateAsync: verifyBankDetails } = useVerifyProposedBankDetails();
  const [isVerifying, setIsVerifying] = useState(false);

  const verify = async (
    investmentAccountGuid: string,
    accountNumber: string,
    ifscCode: string,
    primaryAccountHolderName: string
  ) => {
    try {
      setIsVerifying(true);
      let attempts = 0;
      let result: UnboxPromise<ReturnType<typeof verifyBankDetails>>;
      do {
        // eslint-disable-next-line no-await-in-loop
        result = await verifyBankDetails({
          investmentAccountGuid,
          accountNumber,
          ifscCode,
          accountHolderName: primaryAccountHolderName,
        });

        if (BANK_VERIFICATION_FAILURE_STATUSES.includes(result.status)) {
          throw new Error(result.message);
        }
        if (result.status === "verified") break;
        // eslint-disable-next-line no-await-in-loop
        await sleep(5000);
        attempts += 1;
      } while (attempts <= MAX_VERIFICATION_ATTEMPTS);

      return {
        status: result.status,
        message: result.message,
      };
    } catch (e) {
      if (e instanceof APIError) {
        return {
          status: "failed" as const,
          message: "Failed to verify bank details",
        };
      }
      return {
        status: "failed" as const,
        message: (e as Error).message,
      };
    } finally {
      setIsVerifying(false);
    }
  };

  return { verify, isVerifying };
}
